import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from '../../auth/useTenantContext';
import { useRole } from '../../auth/useRole';
import { usePickListsBoard, type PickListSummary } from '../../api/pickLists';
import { downloadPickListCsv } from '../../api/pickListLifecycle';
import { useAssignmentBoard } from '../../api/assignments';
import {
  PickListHeadFilters, DEFAULT_HEAD_FILTERS, type PickListHeadFiltersValue,
} from '../../components/picklists/PickListHeadFilters';
import { PickListsTable } from '../../components/picklists/PickListsTable';
import { PickListDetailPanel } from '../../components/picklists/PickListDetailPanel';
import type { SortDir } from '../../components/ui/sortable';
import { useFilterSidebar } from '../../layouts/FilterSidebarContext';

const PER_PAGE = 20;

/**
 * bug_fixes_tpicking_development: legge `?status=…` (CSV) dalla query string al
 * mount. Niente più `?partials=true` (il filtro "solo parziali" è stato
 * rimosso). Il valore 'assigned' è un display-status che il consumer filtra
 * post-fetch (`display_status === 'assigned'`).
 */
function readFiltersFromUrl(sp: URLSearchParams): PickListHeadFiltersValue {
  const statusCsv = sp.get('status');
  // bug_fixes_tpicking_development — la card "Evase parzialmente" entra con
  // ?partials=true: attiva `partials_only` e azzera lo stato (i parziali stanno
  // su liste Evase/Confermate, non "Ricevuta").
  const partials = sp.get('partials') === 'true';
  // La card "In attesa di colli" entra con ?awaiting_colli=true. Come per i
  // parziali, lo stato va azzerato: quelle liste sono Evase/Confermate, non
  // "Ricevuta", quindi il filtro di default le nasconderebbe.
  const awaitingColli = sp.get('awaiting_colli') === 'true';
  const senzaStato = partials || awaitingColli;
  return {
    ...DEFAULT_HEAD_FILTERS,
    status: senzaStato ? [] : (statusCsv ? statusCsv.split(',').filter(Boolean) : DEFAULT_HEAD_FILTERS.status),
    partials_only: partials,
    awaiting_colli: awaitingColli,
  };
}

/**
 * Pick-list page (SP-5l accordion, SP-6c board). Detail inline as a level-1
 * accordion. SP-6c: server-side filters + multi-column sort via /admin/pick-lists/board
 * (offset pagination). Default sort: booking_date DESC.
 *
 * bug_fixes_tpicking_development:
 * - PageHeading rimosso (richiesta utente — UI più pulita).
 * - 'assigned' come display-status nel filtro Stato → filtrato client-side
 *   dopo la fetch (matching su display_status='assigned'). NB: il `total`
 *   restituito dal server non riflette questo filtro client-side; la
 *   paginazione potrebbe mostrare meno righe del previsto per pagina.
 *   Trade-off accettabile per evitare di duplicare la logica display_status
 *   nel backend.
 */
export default function PickListsList() {
  const { t } = useTranslation('picklist');
  const { id: idParam } = useParams<{ id: string }>();
  const initialOpen = idParam && /^\d+$/.test(idParam) ? Number(idParam) : null;

  const { token, tenantCode, warehouseId } = useTenantContext();
  // SP-8c — la stampa CSV del dettaglio è riservata al capo magazzino.
  const { isWarehouseAdmin } = useRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(
    () => readFiltersFromUrl(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [filters, setFilters] = useState<PickListHeadFiltersValue>(initialFilters);
  const [sortCol, setSortCol] = useState<string | null>('booking_date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(initialOpen);

  // Sync UI -> URL (replace history).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const statusIsDefault = filters.status.length === DEFAULT_HEAD_FILTERS.status.length
      && filters.status.every((s) => DEFAULT_HEAD_FILTERS.status.includes(s));
    if (filters.status.length > 0 && !statusIsDefault) {
      next.set('status', filters.status.join(','));
    } else {
      next.delete('status');
    }
    if (filters.partials_only) next.set('partials', 'true');
    else next.delete('partials');
    if (filters.awaiting_colli) next.set('awaiting_colli', 'true');
    else next.delete('awaiting_colli');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.partials_only, filters.awaiting_colli]);

  // Operatori per il filtro (dal board del magazzino corrente).
  // Stabilizzo il fallback `?? []` con useMemo per non triggerare loop in
  // useFilterSidebar quando data é null al primo render.
  const operatorsData = useAssignmentBoard(token, tenantCode, warehouseId ?? null).data?.operators;
  const operators = useMemo(() => operatorsData ?? [], [operatorsData]);

  // bug_fixes_tpicking_development — il filtro Stato è ora applicato dal BACKEND
  // sul display_status (vedi PickListExportService::listBoard). Mandiamo i valori
  // così come sono ('open' lo traduce il backend in 'unassigned'); niente più
  // remap né filtro client-side → `total` e paginazione corretti.
  const { data, isLoading } = usePickListsBoard(token, tenantCode, {
    status: filters.status,
    partials_only: filters.partials_only || undefined,
    awaiting_colli: filters.awaiting_colli || undefined,
    external_id_search: filters.external_id_search || undefined,
    list_type: filters.list_type || undefined,
    operator_id: filters.operator_id.length > 0 ? filters.operator_id : undefined,
    list_category: filters.list_category || undefined,
    // SP-8b — filtri cliente/articolo globali.
    customer_code: filters.customer_code || undefined,
    recipient_code: filters.recipient_code || undefined,
    customer_name: filters.customer_name || undefined,
    ean13: filters.ean13 || undefined,
    booking_date_from: filters.booking_date_from || undefined,
    booking_date_to: filters.booking_date_to || undefined,
    sort: sortDir ? (sortCol ?? undefined) : undefined,
    order: sortDir ?? undefined,
    page,
    per_page: PER_PAGE,
  });

  // Il backend ritorna già esattamente le righe filtrate → nessun trim client-side.
  const visibleItems: PickListSummary[] = data?.items ?? [];

  // Tri-state sort: asc → desc → none (torna al default booking_date desc).
  function onSort(col: string): void {
    setOpenId(null);
    setPage(1);
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    if (sortDir === 'desc') { setSortCol('booking_date'); setSortDir('desc'); return; }
    setSortDir('asc');
  }

  // SP-8a — registra PickListHeadFilters nella sidebar globale.
  // Lo state filtri vive ancora qui, il JSX viene renderizzato via portal.
  const sidebarFilters = useMemo(
    () => (
      <PickListHeadFilters
        value={filters}
        operators={operators}
        onChange={(next) => { setFilters(next); setPage(1); setOpenId(null); }}
      />
    ),
    [filters, operators],
  );
  useFilterSidebar(sidebarFilters);

  return (
    <div>
      {isLoading
        ? <p className="text-muted">{t('loading')}</p>
        : <PickListsTable
            rows={visibleItems}
            openId={openId}
            onToggle={(id) => setOpenId((cur) => (cur === id ? null : id))}
            renderExpanded={(id) => <PickListDetailPanel id={id} />}
            sortCol={sortCol}
            sortDir={sortDir}
            onSort={onSort}
            page={page}
            perPage={PER_PAGE}
            total={data?.total ?? 0}
            onPageChange={(p) => { setPage(p); setOpenId(null); }}
            onDownloadCsv={isWarehouseAdmin && token && tenantCode
              ? (id) => { void downloadPickListCsv(id, token, tenantCode, warehouseId); }
              : undefined}
          />}
    </div>
  );
}
